// Single home for reading the Identity-Api audit timeline payload. The endpoint hands back entries
// that are ready to render — a UTC instant, a normalised outcome, a target object and a rendered
// summary — so this module reads the page envelope and gets out of the way. It deliberately does
// NOT interpret an entry: no metadata parsing, no per-action special cases, no locally-maintained
// map of action names. Those all lived here once, each one a place where the UI could disagree with
// the log about what an event meant.
//
// The same shape is served by every audit read: the global timeline, one super admin's history, and
// the per-user activity behind Access Management.

// The Action filter offers the actions an operator actually searches for, plus an "All" sentinel.
// These are stable action CODES the endpoint filters on — the display names come from the entry.
export const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'login.succeeded', label: 'Signed in' },
  { value: 'login.failed', label: 'Failed sign-in' },
  { value: 'login.mfa-challenged', label: 'MFA challenge issued' },
  { value: 'impersonation.started', label: 'Impersonated' },
  { value: 'user.deleted', label: 'Deleted user' },
  { value: 'user.email.changed', label: 'Changed email' },
  { value: 'user.reactivated', label: 'Reactivated user' },
  { value: 'school.created', label: 'Created school' },
  { value: 'school.updated', label: 'Edited school' },
  { value: 'group.created', label: 'Created group' },
  { value: 'group.updated', label: 'Edited group' },
];

export const TARGET_TYPE_OPTIONS = [
  { value: '', label: 'All targets' },
  { value: 'User', label: 'User' },
  { value: 'School', label: 'School' },
  { value: 'Group', label: 'Group' },
];

const EM_DASH = '—';

// Reads one entry into the flat shape the tables render. Every value is taken as given; the only
// work here is defaulting the fields a table cell would otherwise render as "undefined".
export function readAuditEntry(entry, index) {
  return {
    id: entry?.id ?? `${index}`,
    occurredAt: entry?.occurredAt ?? null,
    category: entry?.category ?? null,
    action: entry?.action ?? null,
    actionLabel: entry?.actionLabel || EM_DASH,
    actorEmail: entry?.actor?.email || EM_DASH,
    actorUserId: entry?.actor?.userId ?? null,
    target: entry?.target ?? null,
    outcome: entry?.outcome ?? null,
    outcomeDetail: entry?.outcomeDetail ?? null,
    errorCode: entry?.errorCode ?? null,
    ipAddress: entry?.ipAddress ?? null,
    summary: entry?.summary || EM_DASH,
  };
}

export function extractAuditItems(body) {
  const items = body?.entries;
  return Array.isArray(items) ? items : [];
}

// Reads the paging envelope, falling back to sane defaults so the pager never renders NaN.
export function readAuditPage(body, requestedPage, requestedPageSize) {
  const entries = extractAuditItems(body).map(readAuditEntry);
  const totalCount = Number(body?.totalCount ?? entries.length);
  const page = Number(body?.page ?? requestedPage ?? 1);
  const pageSize = Number(body?.pageSize ?? requestedPageSize ?? entries.length);
  const totalPages = Number(
    body?.totalPages ?? (pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1)
  );
  return { entries, totalCount, page, pageSize, totalPages };
}

// The Target column: the label the log captured when the event happened, else the type and id, else
// an em dash for an event with no target (every sign-in).
export function targetSummary(entry) {
  const target = entry.target;
  if (!target) return EM_DASH;
  if (target.label) return target.label;
  return target.id ? `${target.type} ${target.id}` : target.type;
}

// Renders a UTC instant in the timezone of whoever is looking at it — the entry carries a 'Z', so
// the browser resolves it to local time, and the same row reads correctly in the UK, Pakistan or
// India. Date STYLE stays en-GB (day-first) for consistency with the rest of the admin UI; that is
// a formatting choice and is independent of the timezone.
export function formatAuditTime(value) {
  if (!value) return EM_DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
